import { api } from '../../lib/api';

/**
 * Who an update is for.
 *
 * Two values, matching the column. There is deliberately no `private` - see the
 * migration on why a diary is a different product.
 */
export type Visibility = 'public' | 'followers';

export interface PostImage {
  url: string;
  alt: string | null;
}

export interface Post {
  id: number;
  body: string;
  visibility: Visibility;
  created_at: string;
  edited_at: string | null;
  /**
   * Empty until the worker has processed them.
   *
   * The row is written by the job rather than when the post is, so a post is
   * never pointing at an object that does not exist yet - the words appear
   * immediately and the pictures a second later.
   */
  images: PostImage[];
  /** Whether this reader may edit or remove it. Decided by the server. */
  mine: boolean;
  author: {
    username: string | null;
    display_name: string | null;
    avatar: string | null;
  };
}

/** What one update may say. The server enforces the same number. */
export const MAX_BODY = 2000;

/** And how many pictures may hang off it. Also enforced server-side. */
export const MAX_IMAGES = 4;

export const posts = {
  /**
   * One profile's updates.
   *
   * Viewer-aware on the server: the same URL returns different rows to a
   * stranger and a follower, which is why there is no `visibility` parameter
   * here to get wrong.
   */
  forProfile: (username: string) => api.get<{ items: Post[] }>(`/users/${username}/posts`),

  create: (body: string, visibility: Visibility, images: { key: string; alt?: string }[] = []) =>
    api.post<Post>('/me/posts', { body, visibility, images }),

  /**
   * Ask for somewhere to put an image, then PUT the bytes straight there.
   *
   * The file never passes through our API - the signed URL points at storage,
   * which is what keeps a four-megabyte upload off the request that is also
   * trying to render somebody's profile.
   */
  uploadImage: async (file: File): Promise<string> => {
    const { key, url } = await api.post<{ key: string; url: string }>('/me/posts/images/init', {
      content_type: file.type,
      size_bytes: file.size,
    });

    const put = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!put.ok) {
      throw new Error('The upload did not go through.');
    }

    return key;
  },

  update: (id: number, body: string, visibility: Visibility) =>
    api.patch<Post>(`/me/posts/${id}`, { body, visibility }),

  remove: (id: number) => api.delete(`/me/posts/${id}`),
};
